import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function DayContent() {
  const { courseId, weekNo, dayNo } = useParams();
  const navigate = useNavigate();
  const [day, setDay]         = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [msg, setMsg]         = useState('');

  const load = useCallback(() => {
    api.get(`/courses/${courseId}/structure`).then(r => {
      const weeks = r.data.data.weeks;
      const week  = weeks.find(w => w.week_no === parseInt(weekNo));
      const d     = week?.days.find(d2 => d2.day_no === parseInt(dayNo));
      if (d) {
        api.get(`/days/${d.id}`).then(dr => {
          setDay(dr.data.data);
          setProgress(dr.data.data.progress);
        });
      }
    }).finally(() => setLoading(false));
  }, [courseId, weekNo, dayNo]);

  useEffect(() => { load(); }, [load]);

  const markSection = async (section) => {
    if (progress?.[section]) return;
    await api.post('/progress/section', { course_id: +courseId, week_no: +weekNo, day_no: +dayNo, section });
    setProgress(p => ({ ...p, [section]: 1 }));
  };

  const canComplete = () => {
    if (!progress?.content_read) return false;
    if (day?.quiz && !progress?.quiz_passed) return false;
    if (day?.project) {
      const steps = day.project.steps || [];
      if (steps.length > 0 && !steps.every(s => s.completed)) return false;
    }
    return true;
  };

  const handleComplete = async () => {
    setCompleting(true);
    setMsg('');
    try {
      await api.post('/progress/complete-day', {
        course_id: +courseId, week_no: +weekNo, day_no: +dayNo, day_id: day.id,
        has_video: !!day.youtube_url,
      });
      setMsg('Day completed! Next day unlocked.');
      setTimeout(() => navigate(`/courses/${courseId}`), 1500);
    } catch (e) {
      setMsg(e.response?.data?.message || 'Cannot complete yet. Finish all sections.');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  if (!day)    return <div className="text-center py-5 text-danger">Day not found or locked</div>;

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-4">
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/courses/${courseId}`)}>
          <i className="bi bi-arrow-left" />
        </button>
        <div>
          <h5 className="mb-0 fw-bold">{day.title || `Week ${weekNo} · Day ${dayNo}`}</h5>
          <small className="text-muted">Week {weekNo} · Day {dayNo}</small>
        </div>
      </div>

      {/* Section 1: Text Content */}
      {day.text_content && (
        <ContentSection
          icon="bi-file-text-fill" title="Learning Content" color="primary"
          done={progress?.content_read}
          onRead={() => markSection('content_read')}
        >
          <div className="text-muted" style={{lineHeight:1.8, whiteSpace:'pre-wrap'}}>{day.text_content}</div>
          {!progress?.content_read && (
            <button className="btn btn-primary btn-sm mt-3" onClick={() => markSection('content_read')}>
              <i className="bi bi-check2 me-1" />Mark as Read
            </button>
          )}
        </ContentSection>
      )}

      {/* Section 2: Video */}
      {day.youtube_url && (
        <ContentSection
          icon="bi-play-circle-fill" title={day.youtube_title || 'Video Lesson'} color="danger"
          done={progress?.video_watched}
        >
          <div className="video-wrapper mb-3">
            <iframe
              src={getEmbedUrl(day.youtube_url)}
              title={day.youtube_title || 'Lesson Video'}
              allowFullScreen
            />
          </div>
          {!progress?.video_watched && (
            <button className="btn btn-danger btn-sm" onClick={() => markSection('video_watched')}>
              <i className="bi bi-check2 me-1" />Mark Video as Watched
            </button>
          )}
        </ContentSection>
      )}

      {/* Section 3: Quiz */}
      {day.quiz && (
        <QuizSection
          quiz={day.quiz} courseId={+courseId} weekNo={+weekNo} dayNo={+dayNo} dayId={day.id}
          progress={progress} onComplete={(score, passed) => setProgress(p => ({...p, quiz_score: score, quiz_passed: passed ? 1 : 0}))}
        />
      )}

      {/* Section 4: Project */}
      {day.project && (
        <ProjectSection
          project={day.project} empId={null}
          onStepToggle={(stepId, done) => {
            setDay(d => ({
              ...d,
              project: {
                ...d.project,
                steps: d.project.steps.map(s => s.id === stepId ? {...s, completed: done} : s),
              },
            }));
          }}
        />
      )}

      {/* Complete Day */}
      <div className="content-section">
        {msg && <div className={`alert ${msg.includes('completed') ? 'alert-success' : 'alert-warning'} py-2 small`}>{msg}</div>}
        <button
          className="btn btn-success w-100 py-2 fw-semibold"
          onClick={handleComplete}
          disabled={!canComplete() || completing || progress?.completed}
        >
          {progress?.completed
            ? <><i className="bi bi-check-circle-fill me-2" />Day Completed!</>
            : completing
            ? <><span className="spinner-border spinner-border-sm me-2" />Completing...</>
            : <><i className="bi bi-flag-fill me-2" />Mark Day as Complete</>}
        </button>
        {!progress?.completed && (
          <p className="text-muted small text-center mt-2 mb-0">
            Read content · {day.youtube_url ? 'Watch video · ' : ''}{day.quiz ? 'Pass quiz · ' : ''}{day.project ? 'Complete project' : ''}
          </p>
        )}
      </div>
    </>
  );
}

function ContentSection({ icon, title, color, done, children }) {
  return (
    <div className="content-section">
      <h6>
        <i className={`bi ${icon} text-${color} me-2`} />{title}
        {done && <span className="badge bg-success ms-2 small">Done</span>}
      </h6>
      {children}
    </div>
  );
}

function QuizSection({ quiz, courseId, weekNo, dayNo, dayId, progress, onComplete }) {
  const [answers, setAnswers]   = useState({});
  const [result, setResult]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);

  if (progress?.quiz_passed) {
    return (
      <div className="content-section">
        <h6><i className="bi bi-patch-question-fill text-warning me-2" />Quiz <span className="badge bg-success ms-2 small">Passed ✓ ({progress.quiz_score}%)</span></h6>
        <p className="text-muted small mb-0">You have already passed this quiz.</p>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (Object.keys(answers).length < quiz.questions.length) return alert('Answer all questions first');
    setSubmitting(true);
    try {
      const r = await api.post('/progress/quiz', { course_id: courseId, week_no: weekNo, day_no: dayNo, day_id: dayId, answers });
      setResult(r.data.data);
      setSubmitted(true);
      if (r.data.data.passed) onComplete(r.data.data.score, true);
    } catch (e) {
      alert(e.response?.data?.message || 'Quiz submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="content-section">
      <h6><i className="bi bi-patch-question-fill text-warning me-2" />Quiz
        {result && <span className={`badge ms-2 small bg-${result.passed ? 'success' : 'danger'}`}>{result.score}% — {result.passed ? 'Passed' : 'Failed'}</span>}
      </h6>
      <p className="text-muted small">Passing score: {quiz.passing_score}%</p>
      {quiz.questions.map((q, i) => (
        <div key={q.id} className="mb-4">
          <p className="fw-semibold mb-2">{i + 1}. {q.question}</p>
          {['option1','option2','option3','option4'].map(opt => {
            const val = q[opt];
            const selected = answers[q.id] === val;
            let cls = 'quiz-option';
            if (submitted) {
              if (val === q.correct_answer) cls += ' correct';
              else if (selected) cls += ' wrong';
            } else if (selected) cls += ' selected';
            return (
              <div key={opt} className={cls} onClick={() => !submitted && setAnswers({...answers, [q.id]: val})}>
                <i className={`bi ${selected ? 'bi-check-circle-fill' : 'bi-circle'} me-2`} />{val}
              </div>
            );
          })}
        </div>
      ))}
      {!submitted && (
        <button className="btn btn-warning fw-semibold" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Quiz'}
        </button>
      )}
      {submitted && !result?.passed && (
        <button className="btn btn-outline-warning btn-sm mt-2" onClick={() => { setAnswers({}); setResult(null); setSubmitted(false); }}>
          Retry Quiz
        </button>
      )}
    </div>
  );
}

function ProjectSection({ project, onStepToggle }) {
  const toggleStep = async (stepId, current) => {
    const r = await api.post('/progress/step', { step_id: stepId });
    onStepToggle(stepId, r.data.data.completed);
  };

  const allDone = project.steps.every(s => s.completed);

  return (
    <div className="content-section">
      <h6><i className="bi bi-kanban-fill text-purple me-2" style={{color:'#6f42c1'}} />Project: {project.title}
        {allDone && <span className="badge bg-success ms-2 small">Completed</span>}
      </h6>
      {project.description && <p className="text-muted small mb-3">{project.description}</p>}
      <div>
        {project.steps.map((step, i) => (
          <div key={step.id} className={`day-item mb-2 ${step.completed ? 'completed' : ''}`} onClick={() => toggleStep(step.id, step.completed)} style={{cursor:'pointer'}}>
            <i className={`bi ${step.completed ? 'bi-check-square-fill text-success' : 'bi-square'} day-icon`} />
            <span className={step.completed ? 'text-muted text-decoration-line-through' : ''}>{i + 1}. {step.step_text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function getEmbedUrl(url) {
  const m = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
}
